import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import Sparkline from "@/components/Sparkline";

function statusLabel(score) {
  if (score === null || score === undefined) return "—";
  if (score >= 90) return "Excellent";
  if (score >= 80) return "Good";
  if (score >= 70) return "Needs Attention";
  return "Needs Improvement";
}
function badgeCls(score) {
  if (score >= 90) return "text-emerald-400 border-emerald-400/40 bg-emerald-500/10";
  if (score >= 80) return "text-[color:var(--gold)] border-[color:var(--gold)]/40 bg-[color:var(--gold)]/10";
  if (score >= 70) return "text-[color:var(--warning)] border-[color:var(--warning)]/40 bg-[color:var(--warning)]/10";
  return "text-[color:var(--error)] border-[color:var(--error)]/40 bg-[color:var(--error)]/10";
}

export default function MyDisplayScores() {
  const { user } = useAuth();
  const [zones, setZones] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [historyOpen, setHistoryOpen] = useState({});
  useEffect(() => {
    api.get("/my_display_scores").then(({ data }) => setZones(data.zones || []))
       .catch((e) => { toast.error(formatApiError(e)); setZones([]); });
  }, []);
  if (zones === null) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="my-scores-root">
      <Link to="/dashboard" className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Dashboard</Link>
      <div>
        <div className="label-caps">Zone Owner</div>
        <h1 className="font-heading text-3xl sm:text-4xl flex items-center gap-3"><Sparkles size={26}/> My Display Scores</h1>
        <div className="text-sm text-[color:var(--text-secondary)] mt-1">
          Latest scores for zones you were selected as Zone Owner. Only shown after GM Final Approval.
        </div>
      </div>

      {zones.length === 0 ? (
        <div className="hos-card p-8 text-center text-[color:var(--text-secondary)]">
          No approved Display Scores yet. When a Manager selects you as a Zone Owner and the GM approves the inspection, scores will appear here.
        </div>
      ) : (
        <ul className="space-y-3">
          {zones.map((z) => {
            const ex = expanded[z.zone_id];
            const ho = historyOpen[z.zone_id];
            return (
              <li key={z.zone_id} className="hos-card p-5" data-testid={`score-zone-${z.zone_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-2xl truncate">{z.zone_name}</div>
                    <div className="text-xs text-[color:var(--text-secondary)]">
                      {(z.fandom_names || []).join(", ") || "—"} · Inspection {z.inspection_date} · Approved {formatDateTime(z.final_approved_at)}
                    </div>
                    <div className="text-xs text-[color:var(--text-secondary)] mt-1">Manager: {z.manager_name}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {(z.history || []).length > 0 && (
                      <Sparkline values={[...z.history].reverse().map((h) => h.score).concat([z.score])} />
                    )}
                    <div className={`inline-flex items-baseline gap-1 rounded-md border px-3 py-2 ${badgeCls(z.score)}`} data-testid={`score-badge-${z.zone_id}`}>
                      <span className="font-heading text-3xl">{Math.round(z.score)}</span>
                      <span className="text-[10px] uppercase">/100</span>
                      <span className="text-xs ml-1">· {statusLabel(z.score)}</span>
                    </div>
                  </div>
                </div>

                <button
                  className="mt-3 text-xs text-[color:var(--gold)] hover:underline flex items-center gap-1"
                  onClick={() => setExpanded((s) => ({ ...s, [z.zone_id]: !s[z.zone_id] }))}
                  data-testid={`score-breakdown-${z.zone_id}`}
                >
                  {ex ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                  {ex ? "Hide score details" : "View score details"}
                </button>

                {ex && <Breakdown items={z.breakdown}/>}

                {(z.history || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[color:var(--border)]">
                    <button className="text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] flex items-center gap-1"
                            onClick={() => setHistoryOpen((s) => ({ ...s, [z.zone_id]: !s[z.zone_id] }))}
                            data-testid={`score-history-toggle-${z.zone_id}`}>
                      {ho ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                      {ho ? "Hide history" : `Show history (${z.history.length} previous)`}
                    </button>
                    {ho && (
                      <ul className="mt-2 divide-y divide-[color:var(--border)]">
                        {z.history.map((h) => (
                          <li key={h.check_id} className="py-2 flex items-center justify-between gap-3 text-sm">
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold truncate">{h.check_number}</div>
                              <div className="text-[11px] text-[color:var(--text-secondary)]">{h.inspection_date} · {formatDateTime(h.final_approved_at)}</div>
                            </div>
                            <div className={`inline-flex items-baseline gap-1 rounded-md border px-2 py-1 ${badgeCls(h.score)}`}>
                              <span className="font-heading text-lg">{Math.round(h.score)}</span>
                              <span className="text-[10px] uppercase">/100</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Breakdown({ items }) {
  if (!items || !items.length) {
    return <div className="mt-3 text-xs text-[color:var(--text-secondary)]">Score details not available for this inspection.</div>;
  }
  const total = items.reduce((s, i) => s + (i.weighted_contribution || 0), 0);
  return (
    <div className="mt-3 rounded-md border border-[color:var(--border)] overflow-hidden">
      <table className="w-full text-xs">
        <thead className="text-left">
          <tr className="bg-[color:var(--bg-surface-2)] label-caps">
            <th className="p-2">Standard</th>
            <th className="p-2 text-right">Score</th>
            <th className="p-2 text-right">Weight</th>
            <th className="p-2 text-right">Contribution</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.standard_id} className="border-t border-[color:var(--border)]">
              <td className="p-2">{it.name_snapshot}</td>
              <td className="p-2 text-right">{it.score_percentage === null ? "—" : `${Math.round(it.score_percentage)}%`}</td>
              <td className="p-2 text-right">{Math.round(it.weight_snapshot)}%</td>
              <td className="p-2 text-right font-semibold text-[color:var(--gold)]">
                {it.weighted_contribution === null ? "—" : it.weighted_contribution.toFixed(2)}
              </td>
            </tr>
          ))}
          <tr className="border-t border-[color:var(--border)] bg-[color:var(--bg-surface-2)]">
            <td className="p-2 font-semibold" colSpan={3}>FINAL SCORE</td>
            <td className="p-2 text-right font-heading text-lg text-[color:var(--gold)]">{total.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch (_) { return iso; }
}
